import * as React from 'react';
import { useReducer, useEffect } from 'react';
import { ISelectedTax, ITaxData } from '../types';
import fetchAPI from '../utils/fetchAPI';
import { useSettingsContext } from './settings';

const actions = {
	SET_TAX_OPTIONS: 'SET_TAX_OPTIONS',
	SET_LOCATIONS_OPTIONS: 'SET_LOCATIONS_OPTIONS',
	SELECT_TAX_TERM: 'SELECT_TAX_TERM',
	SELECT_LOCATION: 'SELECT_LOCATION',
	CLEAR_FILTERS: 'CLEAR_FILTERS',
	SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
	SET_MODAL_OPEN: 'SET_MODAL_OPEN',
	SET_MODAL_POST_DISPLAYED: 'SET_MODAL_POST_DISPLAYED',
	PRESELECT_TAX_TERM: 'PRESELECT_TAX_TERM',
	CLEAR_PRESELECTED_TAX: 'CLEAR_PRESELECTED_TAX',
};

interface IFiltersState {
	searchQuery: string;
	tax: ISelectedTax;
	locations: Array<any>;
	selectedTax: ISelectedTax;
	selectedLocation: number;
	initSelectedTax: ISelectedTax;
	modalOpen: boolean;
	modalPostDisplayed: number;
	preSelectedTax: ISelectedTax;
}

interface IReducerAction {
	type: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload?: any;
}

const reducer = (state: IFiltersState, action: IReducerAction) => {
	switch (action.type) {
		case actions.SET_TAX_OPTIONS: {
			return {
				...state,
				tax: action.payload,
			};
		}
		case actions.SET_LOCATIONS_OPTIONS: {
			return {
				...state,
				locations: action.payload,
			};
		}
		case actions.SELECT_LOCATION: {
			return {
				...state,
				selectedLocation: action.payload,
			};
		}
		case actions.SET_SEARCH_QUERY: {
			return {
				...state,
				searchQuery: action.payload,
			};
		}

		case actions.SELECT_TAX_TERM: {
			const selectedTax = { ...state.selectedTax };
			if (
				(Array.isArray(action.payload.term) && action.payload.term.length > 0) ||
				(typeof action.payload.term === 'number' && action.payload.term !== 0) ||
				(typeof action.payload.term === 'string' && action.payload.term !== '')
			) {
				selectedTax[action.payload.tax] = action.payload.term;
			} else if ('undefined' !== typeof selectedTax[action.payload.tax]) {
				delete selectedTax[action.payload.tax];
			}

			return {
				...state,
				selectedTax,
			};
		}

		case actions.PRESELECT_TAX_TERM: {
			const preSelectedTax = { ...state.preSelectedTax };
			if (
				(Array.isArray(action.payload.term) && action.payload.term.length > 0) ||
				(typeof action.payload.term === 'number' && action.payload.term !== 0) ||
				(typeof action.payload.term === 'string' && action.payload.term !== '')
			) {
				preSelectedTax[action.payload.tax] = action.payload.term;
			} else if ('undefined' !== typeof preSelectedTax[action.payload.tax]) {
				delete preSelectedTax[action.payload.tax];
			} else {
				preSelectedTax[action.payload.tax] = [];
			}

			return {
				...state,
				preSelectedTax,
			};
		}

		case actions.CLEAR_PRESELECTED_TAX: {
			return {
				...state,
				preSelectedTax: {},
			};
		}

		case actions.CLEAR_FILTERS: {
			return {
				...state,
				selectedTax: state.initSelectedTax,
				selectedLocation: 0,
				searchQuery: '',
			};
		}

		case actions.SET_MODAL_OPEN: {
			return {
				...state,
				modalOpen: action.payload,
			};
		}

		case actions.SET_MODAL_POST_DISPLAYED: {
			return {
				...state,
				modalPostDisplayed: action.payload,
			};
		}

		default: {
			return state;
		}
	}
};

interface IFiltersContext {
	tax: ITaxData;
	locations: Array<any>;
	selectedTax: ISelectedTax;
	selectedLocation: number;
	preSelectedTax: ISelectedTax;
	searchQuery: string;
	modalOpen: boolean;
	modalPostDisplayed: number;
	selectTaxTerm?: (tax: string, term: number | number[]) => void;
	selectLocation?: (locationId: number) => void;
	preSelectTaxTerm?: (tax: string, term: number | number[]) => void;
	setSearchQuery?: (query: string) => void;
	clearFilters?: () => void;
	clearPreSelectedTax?: () => void;
	setModalOpen?: (open: boolean) => void;
	setModalPostDisplayed?: (postID: number) => void;
}

const FiltersContext = React.createContext<IFiltersContext>({} as IFiltersContext);

interface IProps {
	children: React.ReactNode;
}

const FiltersProvider = ({ children }: IProps) => {
	const settings = useSettingsContext();

	let selectedTax = settings?.initSelectedTax ?? {};

	if (settings.pushSelectionsToUrl && window.location.search) {
		selectedTax = {};
		const url = new URL(window.location.href);
		const entries = url.searchParams.entries();

		for (const entry of entries) {
			const key = entry[0];
			const value = entry[1].split(',').map((i) => Number(i));

			selectedTax[key] = value;
		}
	}

	const initialState = {
		searchQuery: '',
		tax: {},
		locations: [],
		selectedTax,
		selectedLocation: 0,
		initSelectedTax: settings?.initSelectedTax ?? {},
		modalOpen: false,
		modalPostDisplayed: 0,
		preSelectedTax: {},
	};

	const [state, dispatch] = useReducer(reducer, initialState);

	useEffect(() => {
		if (settings.filtersToShow.length) {
			const fetchData = async () => {
				const response = await fetchAPI(
					`${settings.apiBase}/get-taxonomies?post_type=${settings.postType}`
				);
				dispatch({ type: actions.SET_TAX_OPTIONS, payload: response });
			};

			// eslint-disable-next-line no-console
			fetchData().catch(console.error);
		}

		// Fetch locations if locations filter is enabled
		if (settings.filtersToShow.includes('rank_math_locations')) {
			console.log('fetching locations');
			const fetchLocations = async () => {
				const response = await fetchAPI(`${settings.apiBase}/get-locations`);
				console.log('response', response);
				dispatch({ type: actions.SET_LOCATIONS_OPTIONS, payload: response });
			};

			// eslint-disable-next-line no-console
			fetchLocations().catch(console.error);
		}
	}, []);

	useEffect(() => {
		if (settings.pushSelectionsToUrl) {
			const url = new URL(window.location.href);
			url.search = '';

			if (state.searchQuery) {
				url.searchParams.set('s', state.searchQuery);
			}

			const selectedTaxKeys = Object.keys(state.selectedTax);
			if (selectedTaxKeys.length) {
				selectedTaxKeys.forEach((key) => {
					url.searchParams.set(key, state.selectedTax[key].toString());
				});
			}

			window.history.replaceState({}, '', url.toString());
		}
	}, [state]);

	const value = {
		tax: state.tax,
		locations: state.locations,
		selectedTax: state.selectedTax,
		selectedLocation: state.selectedLocation,
		preSelectedTax: state.preSelectedTax,
		initSelectedTax: state.initSelectedTax,
		searchQuery: state.searchQuery,
		modalOpen: state.modalOpen,
		modalPostDisplayed: state.modalPostDisplayed,
		selectTaxTerm: (tax: string, term: number | number[]) => {
			dispatch({ type: actions.SELECT_TAX_TERM, payload: { tax, term } });
		},
		selectLocation: (locationId: number) => {
			dispatch({ type: actions.SELECT_LOCATION, payload: locationId });
		},
		setSearchQuery: (query: string) => {
			dispatch({ type: actions.SET_SEARCH_QUERY, payload: query });
		},
		clearFilters: () => {
			dispatch({ type: actions.CLEAR_FILTERS });
		},
		setModalOpen: (open: boolean) => {
			dispatch({ type: actions.SET_MODAL_OPEN, payload: open });
		},
		setModalPostDisplayed: (postID: number) => {
			dispatch({ type: actions.SET_MODAL_POST_DISPLAYED, payload: postID });
		},
		preSelectTaxTerm: (tax: string, term: number | number[]) => {
			dispatch({ type: actions.PRESELECT_TAX_TERM, payload: { tax, term } });
		},
		clearPreSelectedTax: () => {
			dispatch({ type: actions.CLEAR_PRESELECTED_TAX });
		},
	};

	return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};

const useFiltersContext = () => React.useContext(FiltersContext);

export { FiltersProvider, useFiltersContext };
